import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Download, Printer, RefreshCw, Filter, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import api from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const TrialBalanceReport = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAccessToken } = useAuth();
  const [trialBalanceData, setTrialBalanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedSections, setExpandedSections] = useState({
    assets: true,
    liabilities: true,
    equity: true,
    revenue: true,
    expenses: true
  });
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    fetchTrialBalance();
  }, [isAuthenticated]);

  const fetchTrialBalance = async () => {
    if (!isAuthenticated || !getAccessToken()) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/general-ledger/trial-balance', {
        params: { asOfDate }
      });

      const result = response.data;
      if (result.success) {
        setTrialBalanceData(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch trial balance');
      }
    } catch (err) {
      setError(err.message || err.toString());
      console.error('Error fetching trial balance:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toFixed(2);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const groupByAccountType = (trialBalance) => {
    const grouped = {
      assets: [],
      liabilities: [],
      equity: [],
      revenue: [],
      expenses: [],
      cogs: []
    };

    trialBalance.forEach(item => {
      const category = item.account.accountType?.category || item.account.accountType;
      if (category === 'asset') grouped.assets.push(item);
      else if (category === 'liability') grouped.liabilities.push(item);
      else if (category === 'equity') grouped.equity.push(item);
      else if (category === 'revenue') grouped.revenue.push(item);
      else if (category === 'expense') grouped.expenses.push(item);
      else if (category === 'cost-of-goods-sold') grouped.cogs.push(item);
    });

    return grouped;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const filterAccounts = (accounts) => {
    if (!filterText) return accounts;
    return accounts.filter(item =>
      item.account.accountName.toLowerCase().includes(filterText.toLowerCase()) ||
      item.account.accountNumber.includes(filterText)
    );
  };

  const calculateSectionTotals = (accounts) => {
    const debits = accounts.reduce((sum, item) => sum + item.debitBalance, 0);
    const credits = accounts.reduce((sum, item) => sum + item.creditBalance, 0);
    return { debits, credits };
  };

  const handlePrintReport = () => {
    try {
      const sidebar = document.querySelector('.sidebar');
      const mainContent = document.querySelector('.main-content');
      
      if (sidebar) sidebar.style.display = 'none';
      if (mainContent) {
        mainContent.style.marginLeft = '0';
        mainContent.style.width = '100%';
      }
      
      setTimeout(() => {
        window.print();
        
        setTimeout(() => {
          if (sidebar) sidebar.style.display = '';
          if (mainContent) {
            mainContent.style.marginLeft = '';
            mainContent.style.width = '';
          }
        }, 100);
      }, 50);
      
    } catch (error) {
      console.error('Print error:', error);
      window.print();
    }
  };

  const handleExport = () => {
    if (!trialBalanceData) return;
    
    let csv = 'Account Number,Account Name,Account Type,Debit,Credit\n';
    trialBalanceData.trialBalance.forEach(item => {
      const accountType = item.account.accountType?.category || item.account.accountType;
      csv += `${item.account.accountNumber},"${item.account.accountName}",${accountType},${item.debitBalance},${item.creditBalance}\n`;
    });
    csv += `\nTotal,,,${trialBalanceData.totals.totalDebits},${trialBalanceData.totals.totalCredits}`;
    csv += `\nDifference,,,${trialBalanceData.totals.difference},`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trial-balance-${asOfDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading && !trialBalanceData) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-gray-50">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
        </div>
      </div>
    );
  }

  const groupedAccounts = trialBalanceData ? groupByAccountType(trialBalanceData.trialBalance) : null;
  const isBalanced = trialBalanceData ? Math.abs(trialBalanceData.totals.difference) < 0.01 : true;

  const renderAccountSection = (title, accounts, color, sectionKey) => {
    if (!accounts || accounts.length === 0) return null;
    
    const filteredAccounts = filterAccounts(accounts);
    const sectionTotals = calculateSectionTotals(accounts);

    return (
      <>
        <tr 
          className={`${color} cursor-pointer hover:opacity-80 transition-opacity`}
          onClick={() => toggleSection(sectionKey)}
        >
          <td colSpan="4" className="px-6 py-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-gray-700">{title}</span>
              <div className="flex items-center space-x-4">
                <span className="text-xs text-gray-600">
                  D: {formatCurrency(sectionTotals.debits)} | C: {formatCurrency(sectionTotals.credits)}
                </span>
                {expandedSections[sectionKey] ? 
                  <ChevronUp className="w-4 h-4" /> : 
                  <ChevronDown className="w-4 h-4" />
                }
              </div>
            </div>
          </td>
        </tr>
        {expandedSections[sectionKey] && filteredAccounts.map((item, idx) => (
          <tr 
            key={item.account._id} 
            className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
          >
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
              {item.account.accountNumber}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {item.account.accountName}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
              {item.debitBalance > 0 ? formatCurrency(item.debitBalance) : '-'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
              {item.creditBalance > 0 ? formatCurrency(item.creditBalance) : '-'}
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Trial Balance Report</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate('/client-dashboard')}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Report Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">
              Trial Balance - {trialBalanceData ? formatDate(trialBalanceData.asOfDate) : ''}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {trialBalanceData ? trialBalanceData.trialBalance.length : 0} accounts
            </p>
          </div>
          <div className={`px-4 py-2 rounded-full text-xs font-semibold ${
            isBalanced ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {isBalanced ? '✓ Balanced' : '⚠ Unbalanced'}
          </div>
        </div>

        {/* Summary Cards */}
        {trialBalanceData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800">Total Debits</h3>
              <p className="text-2xl font-bold">{formatCurrency(trialBalanceData.totals.totalDebits)}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-800">Total Credits</h3>
              <p className="text-2xl font-bold">{formatCurrency(trialBalanceData.totals.totalCredits)}</p>
            </div>
            <div className={`p-4 rounded-lg ${isBalanced ? 'bg-green-50' : 'bg-red-50'}`}>
              <h3 className={`text-lg font-semibold ${isBalanced ? 'text-green-800' : 'text-red-800'}`}>
                Difference
              </h3>
              <p className={`text-2xl font-bold ${isBalanced ? 'text-green-900' : 'text-red-900'}`}>
                {formatCurrency(trialBalanceData.totals.difference)}
              </p>
            </div>
          </div>
        )}

        {/* Warning if unbalanced */}
        {trialBalanceData && !isBalanced && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 mr-3 mt-0.5" />
              <div>
                <h3 className="font-semibold">Trial Balance is Unbalanced</h3>
                <p className="text-sm mt-1">
                  The difference between debits and credits is {formatCurrency(Math.abs(trialBalanceData.totals.difference))}. 
                  Please review your journal entries.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              As of Date
            </label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter Accounts
            </label>
            <input
              type="text"
              placeholder="Search by name or number..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-2">Quick Actions</h3>
          <div className="flex space-x-2">
            <button
              onClick={fetchTrialBalance}
              disabled={loading}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handlePrintReport}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Print Report
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Trial Balance Table */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
          </div>
        )}

        {!loading && trialBalanceData && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Debit
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderAccountSection('Assets', groupedAccounts.assets, 'bg-blue-100', 'assets')}
                {renderAccountSection('Liabilities', groupedAccounts.liabilities, 'bg-orange-100', 'liabilities')}
                {renderAccountSection('Equity', groupedAccounts.equity, 'bg-purple-100', 'equity')}
                {renderAccountSection('Revenue', groupedAccounts.revenue, 'bg-green-100', 'revenue')}
                {renderAccountSection('Cost of Goods Sold', groupedAccounts.cogs, 'bg-yellow-100', 'expenses')}
                {renderAccountSection('Expenses', groupedAccounts.expenses, 'bg-red-100', 'expenses')}

                {/* Total Row */}
                <tr className="bg-gray-50 font-bold">
                  <td colSpan="2" className="px-6 py-4 text-right text-sm uppercase tracking-wider">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {formatCurrency(trialBalanceData.totals.totalDebits)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {formatCurrency(trialBalanceData.totals.totalCredits)}
                  </td>
                </tr>

                {/* Difference Row (if unbalanced) */}
                {!isBalanced && (
                  <tr className="bg-red-100">
                    <td colSpan="2" className="px-6 py-3 text-right text-sm font-semibold text-red-900">
                      Difference
                    </td>
                    <td colSpan="2" className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-red-900">
                      {formatCurrency(Math.abs(trialBalanceData.totals.difference))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !trialBalanceData && (
          <div className="text-center py-8">
            <p className="text-gray-500">No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialBalanceReport;